import { useCallback, useEffect, useMemo, useState } from "react";
import { Base } from "../../framework/base";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { handleApiError } from "../../models/store/storeEffects";
import { fetchReportVehicles,
    FuelEmission,
    fetchReportSalaryRowTypes,
    fetchReportEmployees,
    fetchReportCostCenters,
    fetchReportCustomers,
    fetchReportWorkTimeTypes } from "../../store/reportOptionsSlice";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import {
    VehicleEventInfo,
    VehicleEventType,
} from "../../models/vehicle/vehicleEventInfo";
import {
    getVehicleDistance,
    getVehicleTotalFuel,
    VehicleDistanceAndFuelQueryPrototypes,
} from "../../services/vehicleService";
import { ICustomerItem } from "../../models/customer/customerItem";
import { ICostCenterItem } from "../../models/costCenter/costCenterItem";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { FuelType } from "../../models/vehicle/fuelType";
import { ITypedOption } from "../../models/common/typedOption";
import { AnyAction, AsyncThunk } from "@reduxjs/toolkit";

export interface VehicleReportData {
    loading: boolean;
    vehicles: VehicleListItemDto[];
    eventData: VehicleEventData;
    presentVehicles: VehicleListItemDto[];
    fuelEmissions: FuelEmission[];
    customers: ICustomerItem[];
    employees: EmployeeListItemDto[];
    costCenters : ICostCenterItem[];
    presentCustomers : ICustomerItem[];
    presentCostCenters : ICostCenterItem[];
    filterByCustomers: (val: string) => boolean;
    filterByEmployees: (val: string) => boolean;
    filterByCostCenters: (val: string) => boolean;
}

export type VehicleEventData = {
    [key in VehicleEventType]: VehicleEventInfo[];
};

interface IVehicleReportOptionsMapping {
    [key: string]: {
        method: AsyncThunk<void, void, any>;
        attr: string;
    };
}

export const VehicleReportOptionsMapping: IVehicleReportOptionsMapping = {
    vehicles: { method: fetchReportVehicles, attr: "vehicles" },
    employees: { method: fetchReportEmployees, attr: "employees" },
    salaryRowTypes: {
        method: fetchReportSalaryRowTypes,
        attr: "salaryRowTypes",
    },
    costCenters: {
        method: fetchReportCostCenters,
        attr: "costCenters",
    },
    customers: {
        method: fetchReportCustomers,
        attr: "customers",
    },
    workTimeTypes: {
        method: fetchReportWorkTimeTypes,
        attr: "workTimeTypes",
    },
};

type VehicleDataMethod = {
    [key in VehicleEventType]: VehicleDistanceAndFuelQueryPrototypes;
};

const vehicleDataMethods: VehicleDataMethod = {
    [VehicleEventType.Distance]: getVehicleDistance,
    [VehicleEventType.TotalFuel]: getVehicleTotalFuel,
};

export const useVehicleReportData = (
    selectedOptions: string[],
    groupByWorkShiftTimeSlots : boolean
): VehicleReportData => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [eventData, setEventData] = useState<VehicleEventData>(null);

    const options = useAppSelector((state) => state.owner.parameters);
    const fuelEmissions = getFuelTypeEmissions(options);

    const timeRange = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.timeRange
    );

    const vehicles = useAppSelector((state) => state.reportOptions.vehicle.items);
    const customers = useAppSelector(
        (state) => state.reportOptions.customer.items
    );
    const costCenters = useAppSelector((state) => state.reportOptions.costCenters);
    const employees = useAppSelector((state) => state.reportOptions.employees);

    const selectedVehicles = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedEmployees
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCustomers
    );

    const selectedVehicleIds = useMemo(
        () =>
            selectedVehicles?.length > 0
                ? selectedVehicles
                : [],
        [vehicles, selectedVehicles]
    );

    const presentCostCenters = useMemo(
        () =>
        //Check agains selected customers
            selectedCustomers?.length > 0
                ? costCenters?.filter(x=> selectedCustomers?.includes(x.customerId)) ?? []
                : costCenters
        ,
        [selectedCostCenters, selectedCustomers, customers, costCenters]
    );

    const presentCustomers = useMemo(
        () => {
            //Check agains selected costcenters
            if (selectedCostCenters?.length > 0) {
                const selectedCostCenterCustomerIds = costCenters?.filter(x=> selectedCostCenters?.includes(x.id)).map(x => x.customerId);
                return customers?.filter(x=> selectedCostCenterCustomerIds?.includes(x.id)) ?? [];
            }

            return customers;
        },
        [selectedCostCenters, selectedCustomers, customers, costCenters]
    );

    const loadEventData = async(
        groupByWorkShiftTimeSlots: boolean,
        signal?: AbortSignal
    ): Promise<VehicleEventData> => { // Explicitly return a Promise<VehicleEventData>
        const data: VehicleEventData = {
            [VehicleEventType.Distance]: [],
            [VehicleEventType.TotalFuel]: [],
        };

        try {
            const promises = Object.keys(vehicleDataMethods).map(async(k) => {
                try {
                    const result = await (
                        vehicleDataMethods[
                            k
                        ] as VehicleDistanceAndFuelQueryPrototypes
                    )(
                        selectedVehicleIds,
                        Base.dayjsToJsonDateTimeOffset(timeRange[0]),
                        Base.dayjsToJsonDateTimeOffset(
                            Base.dateEndOf(timeRange[1], "day")
                        ),
                        groupByWorkShiftTimeSlots,
                        signal
                    );
                    data[k] = result?.data ?? [];
                } catch (err) {
                    if (!signal?.aborted) {
                        console.error(`Failed ${k}:`, err);
                        handleApiError(err, dispatch);
                    }
                }
            });

            await Promise.allSettled(promises);
            return data; // Return the data instead of setting state here
        } catch (error) {
            console.error("Unexpected error:", error);
            return data; // Return fallback data even on failure
        }
    };

    useEffect(() => {
        // Control logic for loadEventData and loading flag.
        // We don't want to stop loading spinner if aborted as that will hide loading spinner
        // that was started by call that actually made previous call to be aported (when search params change)
        const abortController = new AbortController();
        let isActive = true; // Tracks if the effect is still active (not cleaned up)

        setLoading(true); // Set loading immediately when the effect runs

        loadEventData(groupByWorkShiftTimeSlots, abortController.signal)
            .then((newEventData) => {
                if (isActive) {
                    setLoading(false); // Only update if not aborted
                    setEventData(newEventData);
                }
            })
            .catch((err) => {
                if (err.name !== "AbortError" && isActive) {
                    console.error("Fetch error:", err);
                    setLoading(false); // Handle real errors (not aborts)
                    setEventData(null);
                }
            });

        return () => {
            isActive = false; // Mark as inactive before aborting

            // this will signal related API calls to aborted when component unmounts (calls go to canceled state)
            abortController.abort();
        };
    }, [timeRange, selectedVehicleIds, groupByWorkShiftTimeSlots]);

    useEffect(() => {
        const promises = selectedOptions.map((o) =>
            dispatch(
                VehicleReportOptionsMapping[o].method() as unknown as AnyAction
            )
        );

        return () => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
            promises.forEach((p) => p.abort());
        };
    }, []);


    const filterByCostCenters = useCallback(
        (costCenterId: string) => {
            if (!selectedCostCenters || selectedCostCenters.length === 0)
                return true;
            if (!selectedCostCenters.includes(costCenterId)) return false;
            return true;
        },
        [selectedCostCenters]
    );

    const filterByEmployees = useCallback(
        (employeeId: string) => {
            if (!selectedEmployees || selectedEmployees.length === 0)
                return true;
            if (!selectedEmployees.includes(employeeId)) return false;
            return true;
        },
        [selectedEmployees]
    );

    const filterByCustomers = useCallback(
        (customerId: string) => {
            if (!selectedCustomers || selectedCustomers.length === 0)
                return true;
            if (!selectedCustomers.includes(customerId)) return false;
            return true;
        },
        [selectedCustomers]
    );

    return {
        loading,
        vehicles,
        eventData,
        presentVehicles: vehicles,
        fuelEmissions: fuelEmissions,
        customers: customers,
        employees: employees,
        costCenters: costCenters,
        presentCustomers: presentCustomers,
        presentCostCenters: presentCostCenters,
        filterByCostCenters,
        filterByEmployees,
        filterByCustomers,
    };
};

const buildFuelEmissionType = (fuelType: FuelType, options: ITypedOption[]): FuelEmission => {
    // Find the matching option (case-insensitive comparison)
    const emissionOption = options.find(x =>
        x.code.toLowerCase() === `emissionfactor.fueltype.${fuelType.toString()}`.toLowerCase()
    );

    if (!emissionOption) {
        console.error(`Unable to find emission factor for fuel type ${fuelType.toString()}`);
        return {
            fuelType,
            emissionFactor: -1 // Default/error value
        };
    }

    let emissionFactor : number;
    try {
        emissionFactor = emissionOption.value.toDecimal(true);
    }
    catch
    {
        console.error(`Fuel type emission rating value for fuel type (${FuelType[fuelType]})  is not a number ${FuelType[fuelType]}`);
        return {
            fuelType,
            emissionFactor: -1 // Default/error value
        };
    }

    return {
        fuelType,
        emissionFactor: emissionFactor
    };
};

export const getFuelTypeEmissions = (options : ITypedOption[]): FuelEmission[] => {
    // Dynamically get all enum values except 'Unknown'
    const fuelTypes = (Object.values(FuelType) as FuelType[])
        .filter(type => typeof type === "string");

    // Build emissions for all found types
    const emissions = fuelTypes.map(fuelType =>
        buildFuelEmissionType(fuelType, options)
    );

    return emissions;
};
